import { Button, Checkbox, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag, Typography, message } from 'antd';
import React from 'react';
import { PricingAPI } from '../../../controller/PricingAPI';

// 部門同時是認領範圍與待辦歸屬。一種待辦只能由一個部門負責 -
// 後端會擋重複並回 422，這裡只負責把訊息顯示出來。
// Laravel 驗證失敗的 data.message 是制式的 "The given data was invalid."，
// 真正有用的 (例如哪個部門已佔用那種待辦) 在 data.errors，取第一則顯示。
// 不共用待辦頁的 showRequestError：它的 404 文案講的是「待辦」。
function showRequestError(error) {
    const status = error?.response?.status;
    const data = error?.response?.data;
    if (status === 404) {
        message.error('這個部門已不存在，請重新整理');
        return;
    }
    const first = data?.errors ? Object.values(data.errors)[0] : null;
    if (Array.isArray(first) && first.length > 0) {
        message.error(first[0]);
        return;
    }
    message.error(data?.message || '操作失敗，請稍後再試');
}

function DepartmentPage() {
    const [rows, setRows] = React.useState([]);
    const [types, setTypes] = React.useState([]);
    const [users, setUsers] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [editing, setEditing] = React.useState(null);
    const [saving, setSaving] = React.useState(false);
    const [form] = Form.useForm();

    const refresh = React.useCallback(() => {
        setLoading(true);
        PricingAPI.departments.index()
            .then((response) => setRows(response.data))
            .catch(showRequestError)
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => {
        refresh();
        PricingAPI.mail.notificationTypes.index()
            .then((response) => setTypes(response.data))
            .catch(showRequestError);
        PricingAPI.users.index({}, 1, 999)
            .then((response) => setUsers(response.data.data || response.data))
            .catch(showRequestError);
    }, [refresh]);

    const openModal = (row) => {
        setEditing(row || { id: null });
        form.setFieldsValue({
            name: row ? row.name : '',
            active: row ? row.active : true,
            notification_types: row ? row.notification_types : [],
            user_ids: row ? row.user_ids : [],
        });
    };

    const handleOk = () => {
        form.validateFields().then((values) => {
            setSaving(true);
            const request = editing.id
                ? PricingAPI.departments.update(editing.id, values)
                : PricingAPI.departments.store(values);
            request.then((response) => {
                const released = response?.data?.released_todos_count;
                if (released > 0) {
                    message.success(`已儲存。這個部門沒有成員了，底下 ${released} 筆還沒有人負責的待辦已退回成所有人都看得到的共用待辦。`);
                }
                setEditing(null);
                refresh();
            }).catch(showRequestError).finally(() => setSaving(false));
        }).catch(() => {});
    };

    const handleDelete = (id) => {
        PricingAPI.departments.destroy(id)
            .then(() => {
                message.success('已刪除，原本指派給它的待辦變成沒有部門');
                refresh();
            })
            .catch(showRequestError);
    };

    // 只有會建待辦的排程才能被部門認領 (creates_todo)；只寄通知的 (例如月報) 沒有人可以被派工，表單不列。
    // typeLabel 仍用完整清單，才不會讓歷史資料顯示成代碼。
    const todoTypes = types.filter((t) => t.creates_todo);
    // 表單用的判準：後端 assertTypesNotTaken 不看 active，停用部門也算拿走，所以這裡也不濾 active。
    // 編輯中的部門自己的類型要保持可選，否則沒辦法取消勾選。
    const takenBy = (code) => rows.find(
        (r) => r.id !== (editing && editing.id) && (r.notification_types || []).includes(code),
    );
    const todoOptions = todoTypes.map((t) => {
        const owner = takenBy(t.code);
        return {
            value: t.code,
            disabled: !!owner,
            label: owner
                ? <span>{t.label}<Typography.Text type="secondary" style={{ marginLeft: 4 }}>({owner.name}已負責)</Typography.Text></span>
                : t.label,
        };
    });
    // 摘要的判準不同：只算啟用的部門，因為停用的部門不會被 forNotificationType 選到，實際上沒人負責。
    const unassigned = todoTypes.filter(
        (t) => !rows.some((r) => r.active && (r.notification_types || []).includes(t.code)),
    );

    const typeLabel = (code) => {
        const found = types.find((t) => t.code === code);
        return found ? found.label : code;
    };

    const columns = [
        { title: '部門', dataIndex: 'name', width: 160 },
        {
            title: '狀態',
            dataIndex: 'active',
            width: 90,
            render: (active) => (active ? <Tag color="green">啟用</Tag> : <Tag>停用</Tag>),
        },
        { title: '成員數', dataIndex: 'users_count', width: 90, align: 'right' },
        {
            title: '負責的待辦',
            dataIndex: 'notification_types',
            width: 520,
            render: (codes, row) => {
                if (!codes || !codes.length) {
                    return <Typography.Text type="secondary">無</Typography.Text>;
                }
                // 兩種刻意的降級：沒有成員時待辦退回共用待辦，已停用時
                // Department::forNotificationType 不會選到它。文案寫後果，不重述成員數。
                return (
                    <div>
                        <Space size={[4, 4]} wrap>{codes.map((c) => <Tag key={c}>{typeLabel(c)}</Tag>)}</Space>
                        {row.active === false && (
                            <div><Typography.Text type="warning">已停用，掛著的 {codes.length} 種待辦目前沒有人負責</Typography.Text></div>
                        )}
                        {row.users_count === 0 && (
                            <div><Typography.Text type="warning">負責 {codes.length} 種待辦但沒有成員，那些待辦會變成所有人都看得到的共用待辦</Typography.Text></div>
                        )}
                    </div>
                );
            },
        },
        {
            title: '操作',
            width: 140,
            render: (_, row) => (
                <Space>
                    <Typography.Link onClick={() => openModal(row)}>修改</Typography.Link>
                    <Popconfirm title="確定要刪除這個部門嗎?" onConfirm={() => handleDelete(row.id)}>
                        <Typography.Link type="danger">刪除</Typography.Link>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Space style={{ marginBottom: 12 }}>
                <Button type="primary" onClick={() => openModal(null)}>新增部門</Button>
            </Space>
            {todoTypes.length > 0 && !loading && (
                <div style={{ marginBottom: 12 }}>
                    {unassigned.length === 0
                        ? <Typography.Text type="success">所有會建待辦的排程都已有部門負責</Typography.Text>
                        : (
                            <Typography.Text type="warning">
                                還沒有部門負責的待辦：{unassigned.map((t) => t.label).join('、')}
                            </Typography.Text>
                        )}
                </div>
            )}
            <Table
                rowKey="id"
                loading={loading}
                dataSource={rows}
                columns={columns}
                pagination={false}
                scroll={{ x: 1000 }}
            />
            <Modal
                title={editing && editing.id ? '修改部門' : '新增部門'}
                visible={!!editing}
                onOk={handleOk}
                onCancel={() => setEditing(null)}
                confirmLoading={saving}
                okText="送出"
                cancelText="取消"
                destroyOnClose
                width={640}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="部門名稱" rules={[{ required: true, message: '請輸入部門名稱' }, { max: 50, message: '最多 50 字' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="active" label="啟用" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="user_ids" label="成員">
                        <Checkbox.Group options={users.map((u) => ({ value: u.id, label: u.name }))} />
                    </Form.Item>
                    <Form.Item
                        name="notification_types"
                        label="負責的待辦"
                        extra="排程產生這些待辦時，會自動進入這個部門的共池。一種待辦只能由一個部門負責。"
                    >
                        <Checkbox.Group options={todoOptions} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}

export default DepartmentPage;
